/**
 * In welchem Modus die Zahlungsseite geoeffnet werden darf.
 *
 * "forced-same-tab" heisst NICHT "wir haben es versucht und es ging nicht", sondern
 * "in dieser Umgebung ist ein zweiter Tab nachweislich unmoeglich, also gar nicht
 * erst vorheoeffnen". Ein leerer Tab, den niemand sieht, ist schlimmer als eine
 * saubere Top-Level-Navigation.
 */
export type RedirectCapability = "new-tab" | "forced-same-tab";

/**
 * iOS-In-App-Browser sind WKWebViews. Deren createWebViewWith-Delegate liefert per
 * Default nil, window.open und target=_blank werden also ignoriert.
 *
 * Android-In-App-WebViews (Marker "; wv") mit setSupportMultipleWindows(false), dem
 * Default, blockieren window.open nicht, sondern navigieren die aktuelle Seite. Das
 * ist noch gefaehrlicher als ein null-Rueckgabewert, weil unser Code danach
 * enterOriginPending aufrufen und die gerade gestartete Navigation ueberschreiben
 * wuerde. Beide Familien gehoeren deshalb in dieselbe Liste.
 *
 * Diese Liste ist die EINE Quelle fuer beides: die Redirect-Entscheidung im
 * Payment-Step und den In-App-Banner. Liefen sie auseinander, koennte der Banner
 * "im Browser oeffnen" empfehlen, waehrend der Checkout weiter auf einen zweiten
 * Tab setzt. Alle Marker sind kleingeschrieben, verglichen wird gegen den
 * kleingeschriebenen User-Agent.
 */
const IN_APP_MARKERS = [
	"fban", // Facebook iOS
	"fbav",
	"fb_iab", // Facebook Android
	"instagram",
	"musical_ly", // TikTok
	"bytedancewebview",
	"tiktok",
	"barcelona", // Threads
	"linkedinapp",
	"snapchat",
	"whatsapp",
	"pinterest",
	"telegram-ios",
	"line/",
	"twitter", // Twitter/X iOS, bisher schon vom Banner abgedeckt
	"x-app",
	"gsa/", // Google Search App
	"; wv", // Android WebView
] as const;

/**
 * True, wenn der User-Agent zu einem eingebetteten In-App-Browser gehoert.
 * Reine Funktion: der User-Agent wird am Aufrufort gelesen, nicht hier, damit
 * die Erkennung testbar bleibt.
 */
export function isInAppBrowser(ua: string): boolean {
	const haystack = ua.toLowerCase();
	return IN_APP_MARKERS.some((marker) => haystack.includes(marker));
}

/**
 * Ein leerer oder unbekannter User-Agent bekommt bewusst "new-tab": das Pre-Open
 * wird versucht, und wenn es scheitert, greift die Fallback-Kaskade im
 * Payment-Step. "forced-same-tab" wuerde hier jeden nicht erkannten
 * Desktop-Browser um den besseren Pfad bringen.
 */
export function detectRedirectCapability(ua: string): RedirectCapability {
	return isInAppBrowser(ua) ? "forced-same-tab" : "new-tab";
}

/**
 * Oeffnet diese Zahlart die Zahlungsseite im SELBEN Tab?
 *
 * Die EINE Quelle fuer beide Aufrufstellen im Payment-Step (Erst-Submit und
 * Resume). Liefen sie auseinander, oeffnete ein wiederaufgenommener Vorgang in
 * einem Tab, den der Erst-Submit nie benutzt hat - genau davor warnte der
 * Kommentar an der zweiten Stelle, solange es zwei Ausdruecke waren.
 *
 * `crypto` bleibt bewusst same-tab: die NOWPayments-Invoice-URL traegt kein
 * txn/ch/pm (255-Zeichen-Limit) und lebt von der sessionStorage-Bruecke des
 * Origin-Tabs. Alles andere entscheidet nur noch die Umgebung, nicht die Zahlart.
 */
export function shouldUseSameTab(paymentMethod: string, capability: RedirectCapability): boolean {
	return paymentMethod === "crypto" || capability === "forced-same-tab";
}

/**
 * URL, mit der ein In-App-Browser die Seite an den Standardbrowser des Geraets
 * uebergibt, oder null, wenn fuer diese Plattform kein dokumentiertes Verfahren
 * existiert.
 *
 * Kein Verfahren deckt alle Apps ab, deshalb ist das nur die zweite Stufe einer
 * Kaskade: erst window.open, dann diese URL, und als letzte Stufe der sichtbare
 * Hinweis auf das App-Menue.
 */
/**
 * Die Plattform hinter einem In-App-Browser. Der Banner braucht sie, um seine
 * Ausbruchskaskade richtig zu sortieren: auf Android liefert `window.open` im
 * Standard-WebView kein null, sondern navigiert die aktuelle Seite, also muss
 * dort das Plattform-Schema zuerst versucht werden.
 */
export type InAppPlatform = "android" | "ios" | "unknown";

export function detectInAppPlatform(ua: string): InAppPlatform {
	const haystack = ua.toLowerCase();
	if (haystack.includes("android")) return "android";
	if (haystack.includes("iphone") || haystack.includes("ipad") || haystack.includes("ipod")) return "ios";
	return "unknown";
}

export function buildInAppEscapeUrl(ua: string, url: string): string | null {
	const httpsPrefix = "https://";
	if (!url.startsWith(httpsPrefix)) return null;
	const withoutScheme = url.slice(httpsPrefix.length);

	switch (detectInAppPlatform(ua)) {
		case "android":
			return `intent://${withoutScheme}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
		case "ios":
			return `x-safari-${httpsPrefix}${withoutScheme}`;
		default:
			return null;
	}
}
